import { useCallback, useRef, useEffect } from "react";
import { Howl } from "howler";
import { CAMERA_CONFIG } from "../constants";

type SoundType = "degree" | "timer" | "frontView" | "sideView";

interface SoundPlayerResult {
  playSound: (soundType: SoundType) => void;
  stopAllSounds: (preserveDegreeSound?: boolean) => void;
  stopDegreeSound: () => void;
  cleanup: () => void;
}

const SOUND_PRIORITY: Record<SoundType, number> = {
  timer: 4,
  frontView: 3,
  sideView: 3,
  degree: 1,
};

export const useSoundPlayer = (): SoundPlayerResult => {
  const soundsRef = useRef<Partial<Record<SoundType, Howl>>>({});
  const isInitializedRef = useRef(false);
  const currentlyPlayingRef = useRef<{ sound: Howl; type: SoundType; priority: number } | null>(null);

  const initializeSounds = useCallback(() => {
    if (isInitializedRef.current) return;



    soundsRef.current = {
      degree: new Howl({
        src: [CAMERA_CONFIG.SOUNDS.degree],
        volume: 0.5,
        loop: true,  // Loop degree sound continuously


      }),
      timer: new Howl({
        src: [CAMERA_CONFIG.SOUNDS.timer],
        volume: 0.5,


      }),
      frontView: new Howl({
        src: [CAMERA_CONFIG.SOUNDS.frontView],
        volume: 0.5,


      }),
      sideView: new Howl({
        src: [CAMERA_CONFIG.SOUNDS.sideView],
        volume: 0.5,


      }),
    };


    isInitializedRef.current = true;
  }, []);

  const stopAllSounds = useCallback((preserveDegreeSound = false) => {

    
    let stoppedSounds: string[] = [];
    Object.entries(soundsRef.current).forEach(([key, sound]) => {
      if (sound && sound.playing()) {
        // Skip degree sound if preserving it
        if (preserveDegreeSound && key === 'degree') {
          return;
        }
        sound.stop();
        stoppedSounds.push(key);
      }
    });

    // Only reset currentlyPlaying if we stopped the currently playing sound
    if (currentlyPlayingRef.current && !preserveDegreeSound || 
        (currentlyPlayingRef.current && currentlyPlayingRef.current.type !== 'degree')) {
      currentlyPlayingRef.current = null;
    }

  }, []);

  const playSound = useCallback(
    (soundType: SoundType) => {

      


      if (!isInitializedRef.current) {


        initializeSounds();
      }

      const sound = soundsRef.current[soundType];
      const newPriority = SOUND_PRIORITY[soundType];

      if (!sound) {

        return;
      }

      if (currentlyPlayingRef.current) {
        const currentPriority = currentlyPlayingRef.current.priority;

        if (newPriority < currentPriority) {

          return;
        }



        currentlyPlayingRef.current.sound.stop();
      }


      sound.play();

      currentlyPlayingRef.current = { sound, type: soundType, priority: newPriority };

      sound.once("end", () => {
        if (currentlyPlayingRef.current?.type === soundType) {
          currentlyPlayingRef.current = null;
        }
      });


    },
    [initializeSounds]
  );

  const cleanup = useCallback(() => {
    stopAllSounds();
    Object.values(soundsRef.current).forEach((sound) => {
      if (sound) {
        sound.unload();
      }
    });
    soundsRef.current = {};
    isInitializedRef.current = false;
    currentlyPlayingRef.current = null;
  }, []);

  useEffect(() => {
    return cleanup;
  }, []);

  const stopDegreeSound = useCallback(() => {
    const degreeSound = soundsRef.current.degree;
    if (degreeSound && degreeSound.playing()) {
      degreeSound.stop();

    }
  }, []);

  return { playSound, stopAllSounds, stopDegreeSound, cleanup };
};
